/**
 * The Publish sheet on web (plan 7.6): a 480px dialog (the shared
 * `WebDialog`, a bottom sheet at phone widths) over the canvas panel, the
 * site-publish card, the Sites list or the settings page. Every state comes
 * from `usePublishSheet` and `publish-sheet-model.ts`; this file only draws
 * them. Focus stays inside while it is open (Tab wraps) and goes back to the
 * control that opened it.
 *
 * The address field is always left to right: `https://`, the lowercase
 * input, `.tabbio.site`. Its status line is a polite live region.
 */
import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { usePathname } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { WebDialog } from '../common/WebDialog.web';
import { SpinnerGlyph } from '../common/SpinnerGlyph';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { createChatUpgradeContext } from '@/lib/monetization-context';
import { useUpgradeModal } from '@/providers/useUpgradeModal';
import { useSubscriptionState } from '@/providers/useSubscriptionState';
import { formatArtifactTime } from '../artifacts/artifact-model';
import { nextPlanTier, sitesPolicyUrl, type PublishSheetIntent } from './publish-sheet-model';
import { fixPageWithTabbio, openSitesList } from './site-navigation';
import { usePublishSheet, type PublishSheetController } from './usePublishSheet';

export type PublishSheetProps = {
  artifactId: string;
  intent: PublishSheetIntent;
  suggestedName?: string | null;
  title?: string | null;
  onClose: () => void;
};

export function PublishSheet({ artifactId, intent, suggestedName, title, onClose }: PublishSheetProps) {
  const { t } = useTranslation('common');
  const controller = usePublishSheet({ artifactId, intent, suggestedName, artifactTitle: title });
  const upgradeModal = useUpgradeModal();
  const { snapshot } = useSubscriptionState();
  const pathname = usePathname();
  const view = controller.state.view;

  // Focus returns to the opener (the Share button, the card's Publish).
  useEffect(() => {
    const opener =
      typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    return () => {
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  const upgrade = () => {
    const next = nextPlanTier(snapshot?.tier);
    onClose();
    upgradeModal.open(
      createChatUpgradeContext({
        entryPoint: 'sites.publish_sheet',
        planSet: snapshot?.planSet,
        ...(next ? { recommendedTier: next } : {}),
        returnTo: pathname,
      }),
    );
  };

  const dialogTitle =
    view === 'manage' || view === 'updating' || view === 'unpublishing'
      ? t('chat.artifacts.publish.manage')
      : view === 'rename' || view === 'renaming'
        ? t('chat.artifacts.publish.rename')
        : view === 'history'
          ? t('chat.artifacts.publish.history')
          : t('chat.artifacts.publish.title');
  const back =
    view === 'rename' || view === 'history'
      ? () => controller.dispatch({ type: 'go', view: 'manage' })
      : undefined;

  return (
    <WebDialog title={dialogTitle} onClose={onClose} onBack={back} maxWidth={480} zIndex={1100}>
      <PublishSheetContent
        controller={controller}
        onClose={onClose}
        onUpgrade={upgrade}
        onFix={() => {
          onClose();
          fixPageWithTabbio(artifactId, t('chat.artifacts.publish.fixPrompt'));
        }}
        onUnpublishOther={() => {
          onClose();
          openSitesList();
        }}
      />
    </WebDialog>
  );
}

/** Keeps Tab inside the dialog: from the last control back to the first, and the reverse. */
function trapTab(event: KeyboardEvent<HTMLDivElement>) {
  if (event.key !== 'Tab') return;
  const dialog = event.currentTarget.closest('[role="dialog"]');
  if (!dialog) return;
  const focusable = Array.from(
    dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => element.offsetParent !== null || element === document.activeElement);
  if (focusable.length === 0) return;
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export function PublishSheetContent({
  controller,
  onClose,
  onUpgrade,
  onFix,
  onUnpublishOther,
}: {
  controller: PublishSheetController;
  onClose: () => void;
  onUpgrade: () => void;
  onFix: () => void;
  onUnpublishOther: () => void;
}) {
  const { t } = useTranslation('common');
  const { state } = controller;
  const rootRef = useRef<HTMLDivElement | null>(null);

  // The first field or button takes focus when the view changes.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const target = root.querySelector<HTMLElement>('[data-autofocus]') ?? root.querySelector<HTMLElement>('button:not([disabled]), input');
    target?.focus({ preventScroll: true });
  }, [state.view]);

  let body: ReactNode;
  switch (state.view) {
    case 'loading':
      body = <Progress label={t('loading')} />;
      break;
    case 'publishing':
      body = <Progress label={t('chat.artifacts.publish.publishing')} />;
      break;
    case 'building':
      body = (
        <Progress
          label={t('chat.artifacts.publish.publishing')}
          hint={t('chat.artifacts.publish.buildingHint')}
        />
      );
      break;
    case 'renaming':
      body = <Progress label={t('chat.artifacts.publish.renaming')} />;
      break;
    case 'updating':
      body = <Progress label={t('chat.artifacts.publish.updating')} />;
      break;
    case 'unpublishing':
      body = <Progress label={t('chat.artifacts.publish.unpublishing')} />;
      break;
    case 'live':
      body = <LiveView controller={controller} onClose={onClose} />;
      break;
    case 'manage':
      body = <ManageView controller={controller} />;
      break;
    case 'rename':
      body = <RenameView controller={controller} />;
      break;
    case 'history':
      body = <HistoryView controller={controller} />;
      break;
    default:
      body = (
        <FormView
          controller={controller}
          onUpgrade={onUpgrade}
          onFix={onFix}
          onUnpublishOther={onUnpublishOther}
        />
      );
  }

  return (
    <div ref={rootRef} className="tabbio-publish-sheet" onKeyDown={trapTab}>
      {body}
    </div>
  );
}

function Progress({ label, hint }: { label: string; hint?: string }) {
  return (
    <div className="tabbio-publish-progress" role="status" aria-live="polite">
      <SpinnerGlyph color="var(--text-secondary)" size={22} />
      <span className="tabbio-publish-progress-label">{label}</span>
      {hint ? <span className="tabbio-publish-hint">{hint}</span> : null}
    </div>
  );
}

function Switch({
  checked,
  label,
  hint,
  disabled,
  onChange,
}: {
  checked: boolean;
  label: string;
  hint?: string;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  const hintId = useId();
  return (
    <div className="tabbio-publish-row">
      <div className="tabbio-publish-row-text">
        <span className="tabbio-publish-row-label">{label}</span>
        {hint ? (
          <span id={hintId} className="tabbio-publish-hint">
            {hint}
          </span>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        aria-describedby={hint ? hintId : undefined}
        disabled={disabled}
        className={`tabbio-site-switch${checked ? ' is-on' : ''}`}
        onClick={() => onChange(!checked)}
      >
        <span className="tabbio-site-switch-thumb" />
      </button>
    </div>
  );
}

/** `https://` [name] `.tabbio.site`, always left to right, with its live status line. */
function AddressField({
  value,
  onChange,
  status,
  baseDomain,
  onSubmit,
  onPickSuggestion,
}: {
  value: string;
  onChange: (value: string) => void;
  status: PublishSheetController['form']['status'];
  baseDomain: string;
  onSubmit: () => void;
  onPickSuggestion: (name: string) => void;
}) {
  const { t } = useTranslation('common');
  const inputId = useId();
  const statusId = useId();
  const hintId = useId();
  const suggestions = status.tone === 'error' ? status.suggestions : [];
  return (
    <div className="tabbio-publish-field">
      <label htmlFor={inputId} className="tabbio-publish-row-label">
        {t('chat.artifacts.publish.addressLabel')}
      </label>
      <div className={`tabbio-site-address${status.tone === 'error' ? ' is-invalid' : ''}`} dir="ltr">
        <span className="tabbio-site-address-affix" aria-hidden>
          https://
        </span>
        <input
          id={inputId}
          data-autofocus=""
          className="tabbio-site-address-input"
          dir="ltr"
          value={value}
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          maxLength={40}
          inputMode="url"
          aria-invalid={status.tone === 'error' || undefined}
          aria-describedby={`${statusId} ${hintId}`}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onSubmit();
            }
          }}
        />
        <span className="tabbio-site-address-affix" aria-hidden>
          .{baseDomain}
        </span>
      </div>
      <div id={statusId} className={`tabbio-publish-status is-${status.tone}`} aria-live="polite">
        {status.tone === 'checking' ? <SpinnerGlyph color="var(--text-secondary)" size={14} /> : null}
        {status.tone === 'available' ? <MaterialSymbol name="check" size={16} /> : null}
        {status.tone === 'none' ? null : <span>{t(`chat.artifacts.${status.key}`)}</span>}
      </div>
      {suggestions.length > 0 ? (
        <div className="tabbio-publish-suggestions" role="group" aria-label={t('chat.artifacts.publish.suggestionsLabel')}>
          <span className="tabbio-publish-hint">{t('chat.artifacts.publish.try')}</span>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              className="tabbio-publish-chip"
              dir="ltr"
              onClick={() => onPickSuggestion(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      ) : null}
      <span id={hintId} className="tabbio-publish-hint">
        {t('chat.artifacts.publish.addressHint')}
      </span>
    </div>
  );
}

function FormView({
  controller,
  onUpgrade,
  onFix,
  onUnpublishOther,
}: {
  controller: PublishSheetController;
  onUpgrade: () => void;
  onFix: () => void;
  onUnpublishOther: () => void;
}) {
  const { t } = useTranslation('common');
  const { form, capability } = controller;
  const baseDomain = capability?.baseDomain ?? 'tabbio.site';
  const failure = form.failure;
  const failureText = failure
    ? failure.kind === 'disabled'
      ? t('chat.artifacts.publish.disabled')
      : failure.kind === 'quota'
        ? failure.message || t('chat.artifacts.publish.quota')
        : failure.kind === 'limit'
          ? null
          : t('chat.artifacts.publish.failed')
    : null;

  return (
    <>
      <AddressField
        value={controller.name}
        onChange={controller.setName}
        status={form.status}
        baseDomain={baseDomain}
        onSubmit={controller.publish}
        onPickSuggestion={controller.setName}
      />

      <Switch
        checked={controller.indexable}
        label={t('chat.artifacts.publish.searchToggle')}
        hint={t('chat.artifacts.publish.searchHint')}
        onChange={controller.changeIndexable}
      />

      {form.badge.mode === 'toggle' ? (
        <Switch
          checked={controller.showBadge}
          label={t('chat.artifacts.publish.badgeToggle')}
          onChange={controller.setShowBadge}
        />
      ) : (
        <div className="tabbio-publish-row">
          <span className="tabbio-publish-hint">{t('chat.artifacts.publish.badgeFree')}</span>
          <button type="button" className="tabbio-text-button" onClick={onUpgrade}>
            {t('chat.artifacts.publish.upgrade')}
          </button>
        </div>
      )}

      {form.plan ? (
        <div className={`tabbio-publish-row${form.plan.reached ? ' is-warning' : ''}`}>
          <span className={form.plan.reached ? 'tabbio-publish-row-label' : 'tabbio-publish-hint'}>
            {t('chat.artifacts.publish.limit', { used: form.plan.used, limit: form.plan.limit })}
          </span>
          {form.plan.action === 'upgrade' ? (
            <button type="button" className="tabbio-text-button" onClick={onUpgrade}>
              {t('chat.artifacts.publish.upgrade')}
            </button>
          ) : form.plan.action === 'unpublishOther' ? (
            <button type="button" className="tabbio-text-button" onClick={onUnpublishOther}>
              {t('chat.artifacts.publish.unpublishOther')}
            </button>
          ) : null}
        </div>
      ) : null}

      {form.blockedByFindings ? (
        <div className="tabbio-publish-notice" role="alert">
          <MaterialSymbol name="error" size={20} />
          <div className="tabbio-publish-row-text">
            <span className="tabbio-publish-row-label">{t('chat.artifacts.publish.fixFirst')}</span>
            <span className="tabbio-publish-hint">
              {t('chat.artifacts.publish.findings', { count: form.findingsCount })}
            </span>
          </div>
          <button type="button" className="tabbio-publish-secondary" onClick={onFix}>
            {t('chat.artifacts.publish.fixWithTabbio')}
          </button>
        </div>
      ) : null}

      {failureText ? (
        <p className="tabbio-publish-error" role="alert">
          {failureText}
        </p>
      ) : null}

      <PolicyRow />

      <div className="tabbio-publish-actions">
        <button
          type="button"
          className="tabbio-panel-primary"
          disabled={!form.canPublish}
          onClick={controller.publish}
        >
          {t('chat.artifacts.publish.cta')}
        </button>
      </div>
    </>
  );
}

/** The published sites policy (plan 9.5), in the reader's language, in a new tab. */
function PolicyRow() {
  const { t, i18n } = useTranslation('common');
  return (
    <div className="tabbio-publish-row">
      <span className="tabbio-publish-hint">{t('chat.artifacts.publish.policy')}</span>
      <a
        className="tabbio-text-button"
        href={sitesPolicyUrl(i18n.language)}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('chat.artifacts.publish.policyLink')}
      </a>
    </div>
  );
}

function SiteLink({ url }: { url: string }) {
  const { t } = useTranslation('common');
  return (
    <p className="tabbio-publish-link" dir="ltr" aria-label={t('chat.artifacts.publish.linkLabel')}>
      {url}
    </p>
  );
}

function LiveView({ controller, onClose }: { controller: PublishSheetController; onClose: () => void }) {
  const { t } = useTranslation('common');
  const url = controller.state.site?.url ?? '';
  return (
    <>
      <div className="tabbio-publish-live" role="status">
        <MaterialSymbol name="public" size={24} />
        <h3 className="tabbio-publish-live-title">{t('chat.artifacts.publish.live')}</h3>
      </div>
      <SiteLink url={url} />
      <div className="tabbio-publish-actions is-wrap">
        <button type="button" className="tabbio-panel-download" data-autofocus="" onClick={() => void controller.copyLink()}>
          <MaterialSymbol name="content_copy" size={18} />
          {t('chat.artifacts.publish.copyLink')}
        </button>
        <button type="button" className="tabbio-panel-download" onClick={() => void controller.share()}>
          <MaterialSymbol name="ios_share" size={18} />
          {t('chat.artifacts.publish.shareLink')}
        </button>
        <button type="button" className="tabbio-panel-primary" onClick={controller.open}>
          {t('chat.artifacts.publish.open')}
        </button>
      </div>
      <div className="tabbio-publish-actions is-split">
        <button
          type="button"
          className="tabbio-text-button"
          onClick={() => controller.dispatch({ type: 'go', view: 'manage' })}
        >
          {t('chat.artifacts.publish.manage')}
        </button>
        <button type="button" className="tabbio-text-button" onClick={onClose}>
          {t('chat.artifacts.publish.done')}
        </button>
      </div>
    </>
  );
}

function ManageView({ controller }: { controller: PublishSheetController }) {
  const { t } = useTranslation('common');
  const manage = controller.manage;
  if (!manage) return null;
  const suspended = manage.suspended;
  return (
    <>
      <SiteLink url={manage.url} />
      {manage.previousHost ? (
        <p className="tabbio-publish-hint" dir="auto">
          {t('chat.artifacts.publish.redirecting', { host: manage.previousHost })}
        </p>
      ) : null}
      {suspended ? (
        <p className="tabbio-publish-error" role="alert">
          {t('chat.artifacts.publish.suspended')}
        </p>
      ) : null}
      {suspended ? <PolicyRow /> : null}
      <p className="tabbio-publish-hint">{t('chat.artifacts.publish.views', { count: manage.views30d })}</p>
      <div className="tabbio-publish-actions is-wrap">
        <button type="button" className="tabbio-panel-download" data-autofocus="" onClick={() => void controller.copyLink()}>
          <MaterialSymbol name="content_copy" size={18} />
          {t('chat.artifacts.publish.copyLink')}
        </button>
        <button type="button" className="tabbio-panel-download" onClick={controller.open}>
          <MaterialSymbol name="open_in_new" size={18} />
          {t('chat.artifacts.publish.open')}
        </button>
      </div>

      <div className="tabbio-publish-row">
        <div className="tabbio-publish-row-text">
          <span className="tabbio-publish-row-label">
            {t('chat.artifacts.publish.update')}
            {manage.updateAvailable ? <span className="tabbio-publish-dot" aria-hidden /> : null}
          </span>
          <span className="tabbio-publish-hint">
            {manage.updateAvailable
              ? t('chat.artifacts.publish.updateHint', { number: manage.latestVersionNumber })
              : t('chat.artifacts.publish.upToDate')}
          </span>
        </div>
        <button
          type="button"
          className="tabbio-publish-secondary"
          disabled={suspended || !manage.updateAvailable}
          onClick={controller.update}
        >
          {t('chat.artifacts.publish.update')}
        </button>
      </div>

      <div className="tabbio-publish-row">
        <div className="tabbio-publish-row-text">
          <span className="tabbio-publish-row-label">{t('chat.artifacts.publish.rename')}</span>
          <span className="tabbio-publish-hint">{t('chat.artifacts.publish.renameHint')}</span>
        </div>
        <button
          type="button"
          className="tabbio-publish-secondary"
          disabled={suspended}
          onClick={() => controller.dispatch({ type: 'go', view: 'rename' })}
        >
          {t('chat.artifacts.publish.rename')}
        </button>
      </div>

      <Switch
        checked={controller.indexable}
        label={t('chat.artifacts.publish.searchToggle')}
        hint={t('chat.artifacts.publish.searchHint')}
        disabled={suspended}
        onChange={controller.changeIndexable}
      />

      {controller.state.error && controller.state.error.kind !== 'name' ? (
        <p className="tabbio-publish-error" role="alert">
          {controller.state.error.kind === 'quota' && controller.state.error.message
            ? controller.state.error.message
            : t('chat.artifacts.publish.failed')}
        </p>
      ) : null}

      <div className="tabbio-publish-actions is-split">
        <button
          type="button"
          className="tabbio-text-button"
          onClick={() => controller.dispatch({ type: 'go', view: 'history' })}
        >
          {t('chat.artifacts.publish.history')}
        </button>
        <button
          type="button"
          className="tabbio-text-button is-destructive"
          onClick={() => void controller.unpublish()}
        >
          {t('chat.artifacts.publish.unpublish')}
        </button>
      </div>
    </>
  );
}

function RenameView({ controller }: { controller: PublishSheetController }) {
  const { t } = useTranslation('common');
  const baseDomain = controller.capability?.baseDomain ?? 'tabbio.site';
  return (
    <>
      <AddressField
        value={controller.renameValue}
        onChange={controller.setRenameValue}
        status={controller.renameStatus}
        baseDomain={baseDomain}
        onSubmit={controller.rename}
        onPickSuggestion={controller.setRenameValue}
      />
      <p className="tabbio-publish-hint">{t('chat.artifacts.publish.renameHint')}</p>
      {controller.state.error && controller.state.error.kind !== 'name' ? (
        <p className="tabbio-publish-error" role="alert">
          {t('chat.artifacts.publish.failed')}
        </p>
      ) : null}
      <div className="tabbio-publish-actions">
        <button
          type="button"
          className="tabbio-panel-primary"
          disabled={!controller.canRename}
          onClick={controller.rename}
        >
          {t('chat.artifacts.publish.renameSave')}
        </button>
      </div>
    </>
  );
}

function HistoryView({ controller }: { controller: PublishSheetController }) {
  const { t, i18n } = useTranslation('common');
  const deploys = controller.deploys.data?.deploys ?? [];
  const currentId = controller.state.site?.currentDeploy?.id ?? null;
  if (controller.deploys.isLoading) return <Progress label={t('loading')} />;
  if (deploys.length === 0) return <p className="tabbio-publish-hint">{t('chat.artifacts.publish.historyEmpty')}</p>;
  return (
    <ul className="tabbio-publish-history">
      {deploys.map((deploy) => {
        const version = deploy.versionNumber ?? deploy.number;
        const current = deploy.id === currentId;
        return (
          <li key={deploy.id} className="tabbio-publish-row">
            <div className="tabbio-publish-row-text">
              <span className="tabbio-publish-row-label">
                {t('chat.artifacts.publish.deployRow', {
                  version,
                  time: formatArtifactTime(deploy.createdAt, i18n.language || 'en'),
                })}
              </span>
              <span className="tabbio-publish-hint">
                {current
                  ? t('chat.artifacts.publish.deployCurrent')
                  : deploy.status === 'building'
                    ? t('chat.artifacts.publish.deployBuilding')
                    : deploy.status === 'failed'
                      ? t('chat.artifacts.publish.deployFailed')
                      : ''}
              </span>
            </div>
            {!current && deploy.status === 'ready' ? (
              <button
                type="button"
                className="tabbio-publish-secondary"
                aria-label={t('chat.artifacts.publish.rollbackLabel', { version })}
                onClick={() => controller.rollback(deploy.id)}
              >
                {t('chat.artifacts.publish.rollback')}
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
